import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when pressing Escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when mobile menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const menuItems = [
    { label: 'Services', id: 'services' },
    { label: 'Process', id: 'process', isPage: true },
    { label: 'Pricing', id: 'pricing', isPage: true },
    { label: 'FAQ', id: 'faq', isPage: true },
  ];

  const scrollToSection = (id: string, isPage?: boolean) => {
    if (isPage) {
      navigate(`/${id}`);
      setMobileMenuOpen(false);
      // Scroll to top when navigating to a new page
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }

    const currentPath = window.location.pathname;
    
    if (currentPath !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    
    setMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
      // Scroll to top when navigating to home
      window.scrollTo({ top: 0, behavior: 'instant' });
    } else {
      // If already on home, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleFreeAuditClick = () => {
    const currentPath = window.location.pathname;
    
    if (currentPath !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById('free-audit');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById('free-audit');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    
    setMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0a0a0a]/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <button 
          onClick={handleLogoClick}
          className="flex items-center gap-2 md:gap-3 cursor-pointer hover:opacity-80 transition-opacity duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-lg p-1"
          aria-label="Market Me - Go to homepage"
        >
          <img 
            src="https://public.readdy.ai/ai/img_res/590454d7-738d-464d-a855-ff51c8eda020.png" 
            alt="Market Me Logo" 
            className="h-8 w-8 md:h-10 md:w-10 object-contain"
            width="40"
            height="40"
          />
          <span className="text-xl md:text-2xl font-bold tracking-tight bg-gradient-to-r from-white via-purple-200 to-white bg-clip-text text-transparent">
            Market Me
          </span>
        </button>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center gap-8" role="menubar">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id, item.isPage)}
              className="text-sm font-bold uppercase tracking-wider text-white/80 hover:text-white focus:text-white transition-colors cursor-pointer whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2 py-1"
              role="menuitem"
              aria-label={`Navigate to ${item.label} section`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <button
          onClick={handleFreeAuditClick}
          className="hidden lg:block px-5 py-2 border-2 border-[#8b2bd9] text-white rounded-full hover:bg-[#8b2bd9] focus:bg-[#8b2bd9] transition-all duration-300 cursor-pointer whitespace-nowrap text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 focus:ring-offset-2 focus:ring-offset-black font-semibold"
          aria-label="Get a Free Website Audit"
        >
          Get a Free Audit
        </button>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden text-white text-2xl cursor-pointer w-10 h-10 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded"
          aria-label={mobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
        >
          <i className={mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div 
          id="mobile-menu"
          className="lg:hidden bg-[#0a0a0a]/98 backdrop-blur-lg border-t border-white/10"
          role="menu"
          aria-label="Mobile navigation menu"
        >
          <div className="px-4 py-6 space-y-4">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id, item.isPage)}
                className="block w-full text-left py-3 font-bold text-white/80 hover:text-white focus:text-white transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded px-2"
                role="menuitem"
                tabIndex={0}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={handleFreeAuditClick}
              className="block w-full py-2.5 px-4 border-2 border-[#8b2bd9] text-white rounded-full hover:bg-[#8b2bd9] focus:bg-[#8b2bd9] transition-all duration-300 cursor-pointer text-center mt-4 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:ring-offset-2 focus:ring-offset-black font-semibold text-sm"
              role="menuitem"
              tabIndex={0}
            >
              Get a Free Audit
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
